import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { History, useHistoryStore } from '../../store/useHistoryStore';
import { Button, Label } from 'flowbite-react';
import { HiArrowLeft } from 'react-icons/hi';
import dayjs from 'dayjs';

export default function HistoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const getHistory = useHistoryStore((state) => state.getHistory);
  const loading = useHistoryStore((state) => state.loading);

  const [history, setHistory] = useState<History | null>(null);

  useEffect(() => {
    if (id) {
      loadHistory();
    }
  }, [id]);

  const loadHistory = async () => {
    if (!id) return;
    const historyData = await getHistory(id);
    if (historyData) {
      setHistory(historyData);
    }
  };

  if (!history) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500" style={{ fontFamily: 'Lato, sans-serif' }}>
            {loading ? 'Loading...' : 'History entry not found'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageMeta title="History Details" description="View history information" />
      <PageBreadcrumb pageTitle="History Details" />
      <div className="space-y-6">
        <ComponentCard title="History Information">
          <div className="space-y-6">
            {/* Header with Back Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {history.period}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Priest: {history.priest}
                </p>
              </div>
              <Button
                onClick={() => navigate(`/history`)}
                className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <HiArrowLeft className="mr-2" /> Back to History
                </span>
              </Button>
            </div>

            {/* History Image */}
            {history.image && (
              <div className="w-full">
                <img src={history.image} alt={history.period} className="w-full h-96 object-cover rounded-lg" />
              </div>
            )}

            {/* History Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label value="Period" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {history.period}
                </p>
              </div>
              <div>
                <Label value="Priest" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {history.priest}
                </p>
              </div>
              {history.description && (
                <div className="col-span-2">
                  <Label value="Description" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {history.description}
                  </p>
                </div>
              )}
              <div>
                <Label value="Order" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {history.order}
                </p>
              </div>
              <div>
                <Label value="Created At" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {dayjs(history.createdAt).format('DD MMM YYYY, hh:mm A')}
                </p>
              </div>
            </div>
          </div>
        </ComponentCard>
      </div>
    </>
  );
}
